import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import { isEmpty } from 'lodash'
import { useTheme } from '@emotion/react'
import {
  Section,
  SectionTitle,
  Container,
  Row,
  RowColumn,
} from 'chaoskit/src/components'
import { StylesContainerVariables } from 'chaoskit/src/components/Container'
import { generateGradient } from 'chaoskit/src/assets/styles/utility/gradient'
import { MDXRenderer } from 'gatsby-plugin-mdx'

import Link from '~components/Link'
import Data from '~components/Data'
import TableOfContents from '~components/TableOfContents'
import Image from '~components/Image'
import { textGradientBlockUnder } from '~styles/utility'
import BaseLayout from '~layouts/BaseLayout'
import ArticleFooter from '~partials/global/ArticleFooter'
import HelmetSEO from '~partials/global/HelmetSEO'
import Hero, { HeroContent, HeroTitle } from '~partials/Hero'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'

const ChaptersList = ({ chapters }) => {
  const theme = useTheme()

  return (
    <Section
      css={{
        background: theme.color.panel.base,
        borderTop: theme.border.base,
      }}
    >
      <Container>
        <SectionTitle title="Guide Chapters" />
        <div
          css={{
            display: 'grid',
            gap: theme.space.large,
          }}
        >
          {chapters.map(({ node }) => (
            <div
              key={node.fileAbsolutePath}
              css={{
                position: 'relative',
                transition: `transform ${theme.timing.base} ${theme.transition.bounce}`,
                transformOrigin: 'center center',
                boxShadow: theme.boxShadow.base,
                borderRadius: theme.borderRadius.base,
                background: theme.color.light.base,
                border: theme.border.base,

                '&:hover, &:focus': {
                  transform: 'scale(1.025)',
                },
              }}
            >
              <Link
                to={node.fields.fullUrl}
                css={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: 1,
                }}
              />
              <div
                css={{
                  display: 'grid',

                  [theme.mq.medium]: {
                    gridTemplateColumns: '1fr 2fr',
                  },
                }}
              >
                <div
                  css={{
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center center',
                    backgroundSize: '200% 200%',
                    borderTopRightRadius: theme.borderRadius.base,
                    borderTopLeftRadius: theme.borderRadius.base,

                    [theme.mq.medium]: {
                      borderTopRightRadius: 0,
                      borderBottomLeftRadius: theme.borderRadius.base,
                    },

                    [theme.mq.small__max]: {
                      paddingBottom: '56.25%',
                    },
                  }}
                  style={{
                    backgroundImage: `url(${node.frontmatter.featuredImage})`,
                  }}
                />
                <div
                  css={{
                    padding: theme.space.large,
                    borderBottomRightRadius: theme.borderRadius.base,
                    borderBottomLeftRadius: theme.borderRadius.base,

                    [theme.mq.medium]: {
                      borderBottomLeftRadius: 0,
                      borderTopRightRadius: theme.borderRadius.base,
                    },
                  }}
                >
                  <h4
                    css={[
                      textGradientBlockUnder(theme, {
                        start: theme.color.info.base,
                        stop: theme.color.primary.base,
                      }),
                    ]}
                  >
                    {node.frontmatter.landingPageTitle ||
                      node.frontmatter.title}
                  </h4>
                  <div css={{ fontSize: theme.fontSize.small }}>
                    <MDXRenderer>{node.frontmatter.teaserContent}</MDXRenderer>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </Section>
  )
}

ChaptersList.propTypes = {
  chapters: PropTypes.array.isRequired,
}

const SiteReliabilityEngineeringSingle = ({
  data: {
    article,
    chapters: { edges: chapters },
  },
  pageContext: { previous, next, nodeType },
}) => {
  const theme = useTheme()

  return (
    <BaseLayout headerProps={{ contrast: false }}>
      <HelmetSEO
        title={article.frontmatter.title}
        image={article.frontmatter.socialMediaImage}
        description={article.frontmatter.description}
        type="article"
      />
      <NodeTypeTracker nodeType={nodeType} />
      <Hero contrast={false}>
        <HeroContent>
          <div
            css={{
              paddingLeft: theme.space.base,
              paddingRight: theme.space.base,
              maxWidth: StylesContainerVariables.base,
              display: 'grid',
              margin: '0 auto',
              gap: theme.space.base,
              alignItems: 'center',

              [theme.mq.medium]: {
                gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                gap: 0,
              },
            }}
          >
            <div
              css={{
                [theme.mq.medium]: {
                  gridRow: 1,
                  gridColumn: '7 / span 6',

                  img: {
                    maxWidth: 'none',
                    height: 700,
                  },
                },
              }}
            >
              <Image src={article.frontmatter.featuredImage} alt="" />
            </div>
            <div
              css={{
                position: 'relative',
                zIndex: 1,

                [theme.mq.medium]: {
                  gridRow: 1,
                  gridColumn: '1 / span 6',
                },

                [theme.mq.small__max]: {
                  textAlign: 'center',
                },
              }}
            >
              <HeroTitle size="large">
                <Data>{article.frontmatter.title}</Data>
              </HeroTitle>
              {article.frontmatter.subTitle && (
                <div
                  css={{
                    letterSpacing: theme.letterSpacing.extended,
                    fontWeight: theme.fontWeight.base,
                    fontSize: theme.fontSize.large,
                    textTransform: 'uppercase',
                  }}
                >
                  <Data>{article.frontmatter.subTitle}</Data>
                </div>
              )}
            </div>
          </div>
        </HeroContent>
      </Hero>
      <Section
        css={[
          textGradientBlockUnder(theme, {
            start: theme.color.info.base,
            stop: theme.color.primary.base,
            align: 'center',
          }),

          {
            padding: '0 !important',

            'p:first-of-type': {
              fontSize: theme.fontSize.large,
              marginBottom: theme.space.large,
            },
          },
        ]}
      >
        <Container>
          <MDXRenderer>{article.frontmatter.introContent}</MDXRenderer>
        </Container>
      </Section>
      <Section
        css={{
          background: generateGradient({
            theme,
            start: theme.color.light.base,
            stop: theme.color.panel.dark,
            position: 'to bottom',
          }),
        }}
      >
        <Container>
          <Row gutter={{ base: 'large' }}>
            <RowColumn
              size={{ medium: 3 }}
              css={{
                [theme.mq.small__max]: {
                  display: 'none',
                },
              }}
            >
              <TableOfContents tree={article.tableOfContents} />
            </RowColumn>
            <RowColumn size={{ medium: 9 }}>
              <MDXRenderer>{article.body}</MDXRenderer>
            </RowColumn>
          </Row>
          <ArticleFooter
            navigation={{
              previous: isEmpty(previous) ? null : previous,
              next: isEmpty(next) ? null : next,
            }}
            homeLabel="Site Reliability Engineering"
            homeUrl="/site-reliability-engineering/"
          />
        </Container>
      </Section>
      <ChaptersList chapters={chapters} />
    </BaseLayout>
  )
}

SiteReliabilityEngineeringSingle.propTypes = {
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

export default SiteReliabilityEngineeringSingle

export const pageQuery = graphql`
  query SiteReliabilityEngineeringSingle(
    $slug: String!
    $fileAbsolutePath: String!
  ) {
    article: mdx(
      fields: { slug: { eq: $slug } }
      fileAbsolutePath: { regex: "/src/site-reliability-engineering/" }
    ) {
      ...SiteReliabilityEngineeringArticleFragment
    }

    chapters: allMdx(
      sort: { fields: [frontmatter___priority], order: ASC }
      filter: {
        # Filter out current post
        fileAbsolutePath: {
          regex: "/src/site-reliability-engineering/"
          ne: $fileAbsolutePath
        }
        frontmatter: { title: { ne: "BLUEPRINT" } }
      }
    ) {
      edges {
        node {
          ...SiteReliabilityEngineeringTeaserFragment
        }
      }
    }
  }
`
